/**
 * Assemble one PageSnapshot from the sources the reporter reads (pure; tested).
 *
 * - URL           -> resolvePageRoute (pageType, doctype, record id)
 * - registry      -> the mounted detail record (saved values only) + its active tab
 * - list store    -> the list on screen (only when published on this exact path)
 * - extras        -> app-specific context keyed by source
 */

import type { ActiveDetailPageApi } from "@/contexts/ActiveDetailPageContext";
import type { PublishedList } from "./pageContextStore";
import type { ListContext, PageSnapshot, RecordContext, ResolvedPageRoute } from "./types";

const SENSITIVE_PARAM = /token|secret|password|passwd|sso|code|otp|key|auth|sid|jwt/i;

/** Query string without anything that could be a credential. */
export function sanitizeSearch(search: string): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const kept = new URLSearchParams();
  params.forEach((value, key) => {
    if (!SENSITIVE_PARAM.test(key)) kept.append(key, value);
  });
  const out = kept.toString();
  return out ? `?${out}` : "";
}

/** Use the registered record only when it is the one this page is about. */
function pickActiveRecord(
  route: ResolvedPageRoute,
  api: ActiveDetailPageApi | null,
  openDetail: string | undefined,
): ActiveDetailPageApi | null {
  if (!api) return null;
  const sameDoctype = !route.doctype || api.doctype === route.doctype;
  if (route.pageType === "list") {
    return openDetail && api.docName === openDetail && sameDoctype ? api : null;
  }
  if (route.pageType === "detail" || route.pageType === "create") {
    if (!sameDoctype) return null;
    if (route.name && api.docName && api.docName !== route.name) return null;
    return api;
  }
  return null;
}

function recordFromApi(api: ActiveDetailPageApi, route: ResolvedPageRoute): RecordContext {
  const summary = api.getRecordSummary?.() ?? null;
  const record: RecordContext = { doctype: api.doctype };
  const name = summary?.name ?? api.docName ?? route.name;
  if (name) record.name = name;
  if (summary?.isNew || (!name && route.pageType === "create")) record.isNew = true;
  const title = summary?.title ?? api.getTitle?.() ?? undefined;
  if (title && title !== name) record.title = title;
  if (summary?.docstatus !== undefined) record.docstatus = summary.docstatus;
  if (summary?.status) record.status = summary.status;
  if (summary && Object.keys(summary.fields).length) record.fields = summary.fields;
  return record;
}

export interface SnapshotSources {
  pathname: string;
  search: string;
  route: ResolvedPageRoute;
  label?: string;
  activeRecord: ActiveDetailPageApi | null;
  list: PublishedList | null;
  extra?: Record<string, unknown>;
  ui: PageSnapshot["ui"];
  scope: PageSnapshot["scope"];
  user: PageSnapshot["user"];
  timezone: string;
}

export function buildPageSnapshot(src: SnapshotSources): PageSnapshot {
  const { route } = src;
  const list: ListContext | undefined =
    src.list && src.list.path === src.pathname ? src.list.data : undefined;

  const api = pickActiveRecord(route, src.activeRecord, list?.openDetail);
  let record: RecordContext | undefined;
  if (api) {
    record = recordFromApi(api, route);
  } else if (route.name) {
    // Not registered (yet / non-doctype page): what the URL says
    record = {
      ...(route.doctype ? { doctype: route.doctype } : {}),
      name: route.name,
      ...(route.recordRef ? { ref: route.recordRef } : {}),
    };
  } else if (route.pageType === "create" && route.doctype) {
    record = { doctype: route.doctype, isNew: true };
  }

  const tab = api?.getActiveTab?.() ?? null;
  const doctype = route.doctype ?? api?.doctype ?? list?.doctype;
  const label = src.label ?? doctype;
  const title = record?.title ?? record?.name ?? label;

  return {
    path: src.pathname,
    search: sanitizeSearch(src.search),
    pageType: route.pageType,
    ...(doctype ? { doctype } : {}),
    ...(label ? { label } : {}),
    ...(title ? { title } : {}),
    ...(record ? { record } : {}),
    ...(tab ? { activeTab: tab.label ? { id: tab.id, label: tab.label } : { id: tab.id } } : {}),
    ...(list ? { list } : {}),
    ui: src.ui,
    scope: src.scope,
    user: src.user,
    timezone: src.timezone,
    tenant: src.scope.tenant,
    ...(src.extra ? { extra: src.extra } : {}),
  };
}
